import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import SearchIcon from "@mui/icons-material/Search"
import Box from "@mui/material/Box"
import Breadcrumbs from "@mui/material/Breadcrumbs"
import Collapse from "@mui/material/Collapse"
import InputAdornment from "@mui/material/InputAdornment"
import Link from "@mui/material/Link"
import List from "@mui/material/List"
import ListItemButton from "@mui/material/ListItemButton"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useMemo, useState } from "react"

import { SETTINGS_TABS } from "./settingsTabs"

/** Group + page copy for every settings tab, keyed by SETTINGS_TABS value. */
export const SETTINGS_TAB_META = {
  [SETTINGS_TABS.profile]: {
    label: "Profile",
    group: "Account",
    description: "Manage your personal information and contact details.",
  },
  [SETTINGS_TABS.crmSettings]: {
    label: "Office",
    group: "Account",
    description: "Manage your office locations and brokerage details.",
  },
  [SETTINGS_TABS.team]: {
    label: "Team",
    group: "Account",
    description: "Manage your team members and their access.",
  },
  [SETTINGS_TABS.phoneSms]: {
    label: "Phone & SMS",
    group: "Connections",
    description: "Connect and manage your phone and SMS lines.",
  },
  [SETTINGS_TABS.configureEmail]: {
    label: "Email",
    group: "Connections",
    description: "Connect your email for sending and receiving messages.",
  },
  [SETTINGS_TABS.acuityCalendar]: {
    label: "Calendar",
    group: "Connections",
    description: "Connect your scheduling calendar.",
  },
  [SETTINGS_TABS.campaign]: {
    label: "Recruiting profile & script",
    group: "Recruiting",
    description: "Manage your recruiting campaign profile and script.",
  },
  [SETTINGS_TABS.concierge]: {
    label: "Concierge",
    group: "Recruiting",
    description: "Manage your concierge service settings.",
  },
  [SETTINGS_TABS.session]: {
    label: "Concierge sessions",
    group: "Recruiting",
    description: "Review your concierge session details.",
  },
  [SETTINGS_TABS.planBilling]: {
    label: "Plan & billing",
    group: "Billing",
    description: "Your plan, invoices and MLS purchases in one place.",
  },
}

const GROUP_ORDER = ["Account", "Connections", "Recruiting", "Billing"]

/**
 * Builds the grouped sidebar nav structure from the ordered list of tabs
 * that are currently visible to this user (already filtered by the caller).
 */
export function buildSettingsNavGroups(visibleTabsWithFlags) {
  const groupsByLabel = new Map()
  for (const { tab, disabled, labelOverride, badge } of visibleTabsWithFlags) {
    const meta = SETTINGS_TAB_META[tab]
    if (!meta) continue
    if (!groupsByLabel.has(meta.group)) {
      groupsByLabel.set(meta.group, { label: meta.group, items: [] })
    }
    groupsByLabel.get(meta.group).items.push({
      tab,
      label: labelOverride || meta.label,
      disabled: Boolean(disabled),
      badge,
    })
  }
  return GROUP_ORDER.map((label) => groupsByLabel.get(label)).filter(Boolean)
}

export function SettingsBreadcrumbs({ groupLabel, pageLabel, onGoHome }) {
  return (
    <Breadcrumbs
      separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}
      sx={{ "& .MuiBreadcrumbs-li": { display: "flex" } }}
    >
      <Link
        component="button"
        type="button"
        onClick={onGoHome}
        underline="hover"
        color="text.secondary"
        variant="body2"
      >
        Settings
      </Link>
      {groupLabel && (
        <Typography variant="body2" color="text.secondary">
          {groupLabel}
        </Typography>
      )}
      <Typography variant="body2" color="colors.brand700" fontWeight={700}>
        {pageLabel}
      </Typography>
    </Breadcrumbs>
  )
}

export default function SettingsSidebarNav({ groups, activeTab, onSelect }) {
  const [query, setQuery] = useState("")
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set())

  const filteredGroups = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    if (!normalized) {
      return groups
    }
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          item.label.toLowerCase().includes(normalized)
        ),
      }))
      .filter((group) => group.items.length > 0)
  }, [groups, query])

  const isSearching = query.trim().length > 0

  function toggleGroup(label) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(label)) {
        next.delete(label)
      } else {
        next.add(label)
      }
      return next
    })
  }

  return (
    <Box
      sx={{
        width: { xs: "100%", md: "272px" },
        flexShrink: 0,
        bgcolor: "#fff",
        border: "1px solid",
        borderColor: "colors.gray200",
        borderRadius: "16px",
        p: 1.5,
        alignSelf: "flex-start",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <TextField
        size="small"
        placeholder="Find a setting..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <List
        sx={{
          background: "none",
          backdropFilter: "none",
          boxShadow: "none",
          p: 0,
          "&.MuiList-root": { borderRadius: 0 },
        }}
      >
        {filteredGroups.map((group) => {
          const isOpen = isSearching || !collapsedGroups.has(group.label)
          return (
            <Box key={group.label} sx={{ mb: 1 }}>
              <Box
                onClick={() => toggleGroup(group.label)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  mb: 0.5,
                  borderRadius: "8px",
                  bgcolor: "colors.gray100",
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover": { bgcolor: "colors.gray200" },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",
                    color: "colors.gray600",
                  }}
                >
                  {group.label}
                </Typography>
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{
                    color: "colors.gray500",
                    transition: "transform 150ms ease-in-out",
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              </Box>
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                {group.items.map((item) => {
                  const selected = item.tab === activeTab
                  return (
                    <ListItemButton
                      key={item.tab}
                      selected={selected}
                      disabled={item.disabled}
                      onClick={() => onSelect(item.tab)}
                      sx={{
                        borderRadius: "8px",
                        pl: 1.5,
                        py: 0.75,
                        mb: 0.25,
                        borderLeft: "3px solid transparent",
                        "&.Mui-selected": {
                          bgcolor: "colors.brand50",
                          borderLeftColor: "colors.brand600",
                          "&:hover": { bgcolor: "colors.brand50" },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontWeight: selected ? 700 : 500,
                            color: selected
                              ? "colors.brand700"
                              : "text.secondary",
                          }}
                        >
                          {item.label}
                        </Typography>
                        {item.badge && (
                          <Box
                            component="span"
                            sx={{
                              bgcolor: "colors.brand100",
                              color: "colors.brand700",
                              fontSize: "10px",
                              fontWeight: 700,
                              lineHeight: 1,
                              px: 0.75,
                              py: 0.5,
                              borderRadius: "999px",
                              textTransform: "uppercase",
                              flexShrink: 0,
                            }}
                          >
                            {item.badge}
                          </Box>
                        )}
                      </Box>
                    </ListItemButton>
                  )
                })}
              </Collapse>
            </Box>
          )
        })}
        {filteredGroups.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 1.5, py: 1 }}
          >
            No settings match "{query}".
          </Typography>
        )}
      </List>
    </Box>
  )
}
